import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, message } from "antd";
import type { ComponentProps, ComponentType } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber,
    MultiGrid as ZhMultiGrid,
    DatePicker as ZhDatePicker
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { TIME_PERIOD } from "@zhihaoscm/meta";
import { fmtMoney } from "@/utils/money";
import { loadOilOrderDetail, type OilOrderDetailData } from "./OilOrderDetailPage";

// beta.13 运行时兼容旧 MoneyInput 的 controls 配置，类型声明暂未暴露该字段。
type OilMoneyInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ZhMoneyInput = ZhInputNumber as ComponentType<OilMoneyInputProps>;

/** 预约加油日期沿用旧 Vue：只能选择当天及之后的日期。 */
const isBeforeToday = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
};

export interface OilOrderFormValues {
    oilSiteId: number;
    captainId?: string | number;
    captainName: string;
    captainMobile: string;
    shipId: string;
    oilType: number;
    bookingRefuelingVolume: number;
    bookingRefuelingTime: string;
    bookingRefuelingTimePeriod: number;
    bookingRefuelingPrice: number;
    remark?: string;
    checkType?: number;
}

export interface OilOrderFormPageProps {
    loadDetail?: (id: string) => Promise<OilOrderDetailData>;
    submitBooking?: (values: OilOrderFormValues) => Promise<unknown>;
    submitCheck?: (id: string, values: OilOrderFormValues) => Promise<unknown>;
    saveCheck?: (id: string, values: OilOrderFormValues) => Promise<unknown>;
}

/** 复用 Vue 新增油品订单接口。 */
export async function submitOilOrderBooking(values: OilOrderFormValues) {
    const response = await shippingRequestHttp.service.post("/oil-order-snapshot/booking-refueling", values);
    return response.data;
}

/** 复用 Vue 核对油品订单提交接口。 */
export async function submitOilOrderCheck(id: string, values: OilOrderFormValues) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/check-refueling/${id}`, values);
    return response.data;
}

/** 复用 Vue 核对油品订单保存接口；后端沿用同一核对资源并通过 checkType 区分。 */
export async function saveOilOrderCheck(id: string, values: OilOrderFormValues) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/check-refueling/${id}`, values);
    return response.data;
}

/** 油品订单新增/核对表单，保留 Vue 表单字段、回显和 checkType 提交语义。 */
export function OilOrderFormPage({
    loadDetail = loadOilOrderDetail,
    submitBooking = submitOilOrderBooking,
    submitCheck = submitOilOrderCheck,
    saveCheck = saveOilOrderCheck
}: OilOrderFormPageProps) {
    const location = useLocation();
    const { id: routeId } = useParams();
    const pathId = location.pathname.split("/").filter(Boolean).pop() || "";
    const id = routeId || (pathId && pathId !== "add" ? pathId : "");
    const navigate = useNavigate();
    const [form] = Form.useForm<OilOrderFormValues>();
    const [errorMessage, setErrorMessage] = useState("");
    // 保存 Vue 详情接口返回的站点与船舶回显数据，供 zhihao 摘要组件复用。
    const [detailData, setDetailData] = useState<OilOrderDetailData>();
    // 监听表单值，实时计算旧 Vue 页面中的预估油费。
    const bookingRefuelingPrice = Form.useWatch("bookingRefuelingPrice", form);
    const bookingRefuelingVolume = Form.useWatch("bookingRefuelingVolume", form);
    const shipId = Form.useWatch("shipId", form);
    const bookingRefuelingCost = (Number(bookingRefuelingPrice) || 0) * (Number(bookingRefuelingVolume) || 0);

    useEffect(() => {
        if (!id) return;
        void loadDetail(id)
            .then(result => {
                const order = result.oilOrder;
                setDetailData(result);
                form.setFieldsValue({
                    oilSiteId: Number(result.oilSite?.id),
                    captainName: order.captainName,
                    captainMobile: order.captainMobile,
                    shipId: String(order.shipId || ""),
                    oilType: Number(order.oilType),
                    bookingRefuelingVolume: order.bookingRefuelingVolume,
                    bookingRefuelingTime: order.bookingRefuelingTime,
                    bookingRefuelingTimePeriod: Number(order.bookingRefuelingTimePeriod),
                    bookingRefuelingPrice: order.bookingRefuelingPrice,
                    remark: order.remark
                });
            })
            .catch(() => setErrorMessage("油品订单回显失败"));
    }, [form, id, loadDetail]);

    const onFinish = async (values: OilOrderFormValues, checkType = 1) => {
        try {
            if (id) {
                const payload = { ...values, checkType };
                if (checkType === 2) await saveCheck(id, payload);
                else await submitCheck(id, payload);
            } else await submitBooking(values);
            message.success(id && checkType === 2 ? "已保存" : "提交成功");
            navigate(-1);
        } catch {
            setErrorMessage("油品订单提交失败");
        }
    };

    return (
        <section aria-label="oil-order-form-page" className="flex min-h-full flex-col gap-4">
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <Card title={id ? "核对油品订单" : "新增油品订单"}>
                <Form form={form} layout="vertical" onFinish={values => void onFinish(values)}>
                    <div className="section-title mb-12">预约信息</div>
                    <ZhGrid cols={4} gap={[32, 32]}>
                        <Form.Item
                            label="联系人"
                            name="captainName"
                            rules={[{ required: true, message: "请输入联系人" }]}
                        >
                            <Input />
                        </Form.Item>
                        <ZhGridItem>
                            <Form.Item
                                label="联系方式"
                                name="captainMobile"
                                rules={[
                                    { required: true, message: "请输入联系方式" },
                                    { pattern: /^1\d{10}$/, message: "请输入正确的手机号" }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item
                                label="预约船舶"
                                name="shipId"
                                rules={[{ required: true, message: "请选择预约船舶" }]}
                            >
                                <Input />
                            </Form.Item>
                            {shipId || detailData?.oilOrder.shipId ? (
                                <div className="info-wrap">
                                    <ZhMultiGrid>
                                        <ZhGrid cols={3}>
                                            <ZhBaseItem
                                                label="MMSI"
                                                value={String(shipId || detailData?.oilOrder.shipId)}
                                            />
                                        </ZhGrid>
                                    </ZhMultiGrid>
                                </div>
                            ) : null}
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item
                                label="预约站点"
                                name="oilSiteId"
                                rules={[{ required: true, message: "请选择预约站点" }]}
                            >
                                <InputNumber min={1} />
                            </Form.Item>
                            {detailData?.oilSite?.id ? (
                                <div className="info-wrap">
                                    <ZhMultiGrid>
                                        <ZhGrid cols={3}>
                                            <ZhBaseItem label="站点地址" value={detailData.oilSite.address} />
                                            <ZhBaseItem label="品牌" value={detailData.oilSite.brand} />
                                            <ZhBaseItem label="站点联系方式" value={detailData.oilSite.phone} />
                                        </ZhGrid>
                                    </ZhMultiGrid>
                                </div>
                            ) : null}
                        </ZhGridItem>
                    </ZhGrid>
                    <div className="section-title mb-12">加油信息</div>
                    <ZhGrid cols={4} gap={[32, 32]}>
                        <Form.Item
                            label="油品类型"
                            name="oilType"
                            rules={[{ required: true, message: "请选择油品类型" }]}
                        >
                            <Select
                                options={[
                                    { value: 1, label: "0号柴油" },
                                    { value: 2, label: "轻质燃油" }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            label="预估加油量"
                            name="bookingRefuelingVolume"
                            rules={[
                                { required: true, message: "请输入预估加油量" },
                                { type: "number", min: 0.01, max: 9999.99 }
                            ]}
                        >
                            <ZhMoneyInput controls={false} precision={2} suffix="吨" />
                        </Form.Item>
                        <Form.Item
                            label="预约加油时间"
                            name="bookingRefuelingTime"
                            rules={[{ required: true, message: "请选择预约加油时间" }]}
                        >
                            <ZhDatePicker
                                aria-label="预约加油时间"
                                className="w-full"
                                disabledDate={isBeforeToday}
                                format="YYYY-MM-DD"
                                placeholder="请选择预约加油时间"
                                type="date"
                                valueFormat="YYYY-MM-DDT00:00:00"
                                onChange={() => {
                                    // 日期变化后清空旧时间段，保持 Vue 重新选择时段的业务边界。
                                    form.setFieldValue("bookingRefuelingTimePeriod", undefined);
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="预约时间段"
                            name="bookingRefuelingTimePeriod"
                            rules={[{ required: true, message: "请选择时间段" }]}
                        >
                            <Select
                                // 时间段文案沿用 Vue TIME_PERIOD 枚举，保证提交 code 与用户看到的时段一致。
                                options={TIME_PERIOD.options.map(item => ({ value: item.value, label: item.label }))}
                            />
                        </Form.Item>
                        <Form.Item
                            label="油价"
                            name="bookingRefuelingPrice"
                            rules={[
                                { required: true, message: "请输入油价" },
                                { type: "number", min: 0.01, max: 999999.99 }
                            ]}
                        >
                            <ZhMoneyInput controls={false} precision={2} suffix="元/吨" />
                        </Form.Item>
                        <ZhGridItem>
                            <Form.Item label="预估油费">
                                <span>{fmtMoney(bookingRefuelingCost, "元")}</span>
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                    <Form.Item label="备注" name="remark">
                        <Input.TextArea maxLength={200} />
                    </Form.Item>
                    <Space>
                        <Button htmlType="submit" type="primary">
                            提交
                        </Button>
                        {id ? (
                            <Button
                                htmlType="button"
                                onClick={() => void form.validateFields().then(values => onFinish(values, 2))}
                            >
                                保存
                            </Button>
                        ) : null}
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}

export default OilOrderFormPage;
